// pricing-card.tsx
'use client';

import { Check } from 'lucide-react';
import { motion } from 'framer-motion';
import { useRouter } from 'next/navigation';
import toast from 'react-hot-toast';

import { Card, CardContent, CardFooter, CardHeader } from '@/slw/shared/ui/card';
import { Button } from '@/slw/shared/ui/button';
import { PackageOnCard } from '@/slw/entities/package';
import { useAuthStore } from '@/slw/shared/stores';

interface PricingCardProps {
  packagePlan: PackageOnCard;
  delay?: number;
}

export function PricingCard({ packagePlan, delay = 0 }: PricingCardProps) {
  const router = useRouter();
  const { user } = useAuthStore();

  const handleSelectPackage = () => {
    if (user?.id) {
      router.push(`/checkout?packageId=${packagePlan.id}`);
    } else {
      toast.error('You need to login before listing a product.');
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay }}
      className='relative'
    >
      <Card
        className={`h-full flex rounded-none rounded-b-md overflow-hidden relative
            flex-col shadow-none boder border-[#EBE9E5] 
            ${!!packagePlan.recommendToTheTop ? 'border-primary bg-[#F8F8F7]' : ''}`}
      >
        {!!packagePlan.recommendToTheTop && (
          <div
            className='absolute top-6 right-[-90px] w-66 bg-white text-primary 
          text-xs font-semibold uppercase py-2 transform rotate-45 z-10 text-center'
          >
            Recommended
          </div>
        )}
        <CardHeader className='bg-[#BA9C64] text-white text-center p-6 space-y-0'>
          <h3 className='text-[2rem] font-bold leading-[100%] mb-2 tracking-wider'>{packagePlan.name}</h3>
          <h1 className='text-[2.5rem] font-bold leading-[120%] mb-1 tracking-wider'>${packagePlan.price}</h1>
          <h5 className='text-[1.25rem] font-medium leading-[140%] tracking-wide'>/ For 1 Slot</h5>
        </CardHeader>
        <CardContent className='flex-grow p-6 pb-0'>
          <ul className='space-y-4'>
            {packagePlan.features ? (
              packagePlan.features.map((feature, index) => (
                <li key={index} className='flex items-start justify-between text-sm font-normal leading-[150%]'>
                  <div>
                    <span className=''>{feature.name}</span>
                    {feature.description && (
                      <p className='text-xs font-normal leading-[120%] text-muted-foreground'>{feature.description}</p>
                    )}
                  </div>
                  {feature.included ? (
                    <div className='w-5 h-5 bg-green-500 rounded-full flex items-center justify-center'>
                      <Check className='w-3 h-3 text-white' />
                    </div>
                  ) : feature.value ? (
                    <span className='text-right '>{feature.value}</span>
                  ) : null}
                </li>
              ))
            ) : (
              <li className='text-sm font-normal leading-[150%]'>No features available</li>
            )}
          </ul>
        </CardContent>
        <CardFooter className='p-6 pb-12'>
          <Button
            color='primary'
            onClick={handleSelectPackage}
            variant={packagePlan.recommendToTheTop ? 'default' : 'outline'}
            className={'w-full '}
            size='lg'
          >
            BUY NOW
          </Button>
        </CardFooter>
      </Card>
    </motion.div>
  );
}
